import {
  AssignmentState,
  SectionState,
} from '@breakoutlearning/firebase-repository/types'
import classNames from 'classnames'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { BreakoutAlert } from 'components/design-system/BreakoutAlert'
import { BreakoutPill } from 'components/design-system/BreakoutPill'
import { CheckIcon } from 'components/icons/Check'
import { TriangleAlertIcon } from 'components/icons/TriangleAlert'
import { FullPageSpinner } from 'components/Spinner'
import { useInstructorAssignmentCubit } from 'hooks/cubits/instructorAssignment'
import { useInstructorSectionCubit } from 'hooks/cubits/instructorSection'
import { useRootStore } from 'hooks/rootStore'
import { useTranslationTyped } from 'i18n/i18n'
import { observer } from 'mobx-react-lite'
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Trans } from 'react-i18next'
import { GradingLineItemSelect } from './GradingLineItemSelect'

export const BrightspaceConfirmationPage = observer(
  function BrightspaceConfirmationPage() {
    const assignmentCubit = useInstructorAssignmentCubit()
    const sectionCubit = useInstructorSectionCubit()
    const { tt } = useTranslationTyped()
    const rootStore = useRootStore()
    const sectionState = sectionCubit.section.data?.sectionState
    const assignmentState = assignmentCubit.assignment.data?.assignmentState

    const assignmentData = assignmentCubit.assignment.dataToJs

    const ltiDomain = assignmentData.ltiDomain
    const ltiCourseId = assignmentData.ltiCourseId
    const ltiAssignmentId = assignmentData.ltiAssignmentId
    const [showConfirmationPage, setShowConfirmationPage] = useState(false)
    const [refreshError, setRefreshError] = useState(false)

    // we don't want to auto confirm if some data happens to change after confirmation page loads
    // instead wait for them to hit "go to assignment" button
    const performedAutoConfirmCheck = useRef(false)

    const ltiPageUrl =
      Boolean(ltiDomain) &&
      Boolean(ltiCourseId) &&
      Boolean(ltiAssignmentId) &&
      `https://${ltiDomain}/d2l/le/content/${ltiCourseId}/viewContent/${ltiAssignmentId}/View`

    const refreshParentPage = useCallback(() => {
      if (!ltiPageUrl) return false
      try {
        if (window.parent && window.parent !== window) {
          window.parent.location.href = ltiPageUrl
        } else {
          window.location.href = ltiPageUrl
        }
        return true
      } catch (error) {
        setRefreshError(true)
        return false
      }
    }, [ltiPageUrl])

    const onClick = useCallback(async () => {
      if (
        sectionState === SectionState.unconfigured ||
        sectionState === SectionState.notStarted
      ) {
        await sectionCubit.markAsInProgress()
      }
      if (assignmentState === AssignmentState.unconfigured) {
        await assignmentCubit.markAsActive()
      }

      // in regular mode, we will redirect to the assignment page
      rootStore.navigateToInstructorAssignmentPage(
        assignmentCubit.sectionId,
        assignmentCubit.assignmentId
      )
    }, [
      sectionState,
      assignmentState,
      rootStore,
      assignmentCubit,
      sectionCubit,
    ])

    const assignment = assignmentCubit.assignment

    const needsToSelectGradingItemInitial = useMemo(() => {
      return assignment.ltiAssignmentNeedsGradingLineItemId
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignment.isLoaded])

    const needsToSelectGradingItemCurrent =
      assignment.ltiAssignmentNeedsGradingLineItemId

    const hasGrading = assignment.hasGrading
    const expiresAt = assignment.data.expiresAt
    const expiresAtMissing = !expiresAt
    const lineItemMissing = assignment.data.ltiLineItemMissing
    const lineItemIsSet = !lineItemMissing
    const shouldBlockOnGrade =
      assignment.hasGrading &&
      (lineItemMissing || needsToSelectGradingItemInitial)
    const expiresAtIsInThePast = expiresAt && expiresAt < new Date()
    const expiresAtIsValid = !expiresAtMissing && !expiresAtIsInThePast

    useLayoutEffect(() => {
      if (!assignmentCubit.allDataLoaded || performedAutoConfirmCheck.current)
        return

      performedAutoConfirmCheck.current = true

      // Auto-confirm if all conditions are met (bypass confirmation mode is now the default)
      if (!assignment.hasGrading && expiresAtIsValid) {
        onClick()
        return
      }
      if (expiresAtIsValid && !shouldBlockOnGrade) {
        onClick()
        return
      }
      setShowConfirmationPage(true)
    }, [
      assignmentCubit.allDataLoaded,
      assignment.hasGrading,
      expiresAtIsValid,
      lineItemIsSet,
      shouldBlockOnGrade,
      onClick,
    ])

    if (!assignmentCubit.assignment || !assignmentCubit.allDataLoaded) {
      return <FullPageSpinner />
    }

    if (!showConfirmationPage) {
      return <FullPageSpinner />
    }

    const shouldRefreshParent =
      ltiPageUrl &&
      (expiresAtMissing || shouldBlockOnGrade || expiresAtIsInThePast)

    return (
      <div className="lti-configuration min-h-full w-full">
        <div className="">
          <img
            src="/assets/images/logo.png"
            width={64}
            className="absolute right-8 top-8"
            alt={tt.breakout_learning()}
          />
        </div>
        <div className="lti-plant flex min-h-full w-full flex-col items-center justify-center">
          <div className="max-w-[800px] text-center">
            <div className="mb-3 text-headline-large">
              {tt.lti.configuration.invalid_brightspace_title()}
            </div>
            <div className="mb-4 text-body-large">
              {hasGrading
                ? tt.lti.configuration.invalid_brightspace_description()
                : tt.lti.configuration.invalid_brightspace_description_no_grading()}
            </div>
            <div
              className={classNames('', {
                'grid grid-cols-2 gap-2': hasGrading,
                'flex flex-row justify-center': !hasGrading,
              })}
            >
              {/* Dates Box */}
              <div className="flex max-w-[360px] flex-col justify-between rounded-2xl bg-core-tertiary px-8 py-7">
                <div className="text-body-large">
                  <div className="mb-3 flex flex-row items-center justify-between">
                    <div className="text-title-large">
                      {tt.lti.configuration.brightspace_add_dates()}
                    </div>
                    <BreakoutPill
                      label={tt.lti.configuration.required()}
                      kind="primary"
                      dot
                      dotClassName="bg-core-success"
                    />
                  </div>
                  <div className="mb-3 text-left text-body-large">
                    <Trans
                      i18nKey="lti.configuration.brightspace_dates_instructions"
                      components={{
                        p: <p className="my-2 text-body-large" />,
                        b: <strong />,
                        ul: <ul />,
                        ol: <ol className="ml-4 list-decimal" />,
                        li: <li className="pb-1" />,
                      }}
                    />
                  </div>
                  {expiresAtMissing && (
                    <div className="flex flex-row items-center gap-1 text-left text-label-large text-core-error">
                      <TriangleAlertIcon
                        className="stroke-core-error"
                        size={16}
                      />
                      {tt.lti.configuration.brightspace_dates_warning()}
                    </div>
                  )}
                  {expiresAtIsInThePast && (
                    <div className="flex flex-row items-center gap-1 text-left text-label-large text-core-error">
                      <TriangleAlertIcon
                        className="stroke-core-error"
                        size={16}
                      />
                      {tt.lti.configuration.brightspace_expires_at_is_in_past()}
                    </div>
                  )}
                  {!expiresAtMissing && !expiresAtIsInThePast && (
                    <div className="flex flex-row items-center gap-1 text-left text-label-large text-core-success">
                      <CheckIcon className="stroke-core-success" size={16} />
                      {tt.lti.configuration.brightspace_dates_detected()}
                    </div>
                  )}
                </div>
              </div>
              {/* Line Item Box */}
              {hasGrading && !needsToSelectGradingItemInitial && (
                <div className="flex max-w-[360px] flex-col justify-between rounded-2xl bg-core-tertiary px-8 py-7">
                  <div className="text-body-large">
                    <div className="mb-3 flex flex-row items-center justify-between">
                      <div className="text-title-large">
                        {tt.lti.configuration.brightspace_add_grade_item()}
                      </div>
                      <BreakoutPill
                        label={tt.lti.configuration.required()}
                        kind="primary"
                        dot
                        dotClassName="bg-core-success"
                      />
                    </div>
                    <div className="mb-3 text-left text-body-large">
                      <Trans
                        i18nKey="lti.configuration.brightspace_add_grade_item_instructions"
                        components={{
                          p: <p className="my-2 text-body-large" />,
                          b: <strong />,
                          ul: <ul />,
                          ol: <ol className="ml-4 list-decimal" />,
                          li: <li className="pb-1" />,
                        }}
                      />
                    </div>
                  </div>
                  {lineItemMissing && (
                    <div className="flex flex-row items-center gap-1 text-left text-label-large text-core-error">
                      <TriangleAlertIcon
                        className="stroke-core-error"
                        size={16}
                      />
                      {tt.lti.configuration.brightspace_grade_item_missing()}
                    </div>
                  )}
                  {!lineItemMissing && (
                    <div className="flex flex-row items-center gap-1 text-left text-label-large text-core-success">
                      <CheckIcon className="stroke-core-success" size={16} />
                      {tt.lti.configuration.brightspace_grade_item_detected()}
                    </div>
                  )}
                </div>
              )}
              {hasGrading && needsToSelectGradingItemInitial && (
                <div className="flex max-w-[360px] flex-col gap-3 rounded-2xl bg-core-tertiary px-8 py-7">
                  <div className="text-body-large">
                    <div className="mb-3 flex flex-row items-center justify-between">
                      <div className="text-title-large">
                        {tt.lti.configuration.brightspace_select_grade_item()}
                      </div>
                      <BreakoutPill
                        label={tt.lti.configuration.required()}
                        kind="primary"
                        dot
                        dotClassName="bg-core-success"
                      />
                    </div>
                    <div className="text-left text-body-large">
                      {tt.lti.configuration.brightspace_select_grade_item_instructions()}
                    </div>
                  </div>
                  <div className="h-full w-full flex-grow">
                    <GradingLineItemSelect
                      assignment={assignmentCubit.assignment}
                      onChange={(ltiGradingLineItemId) =>
                        assignmentCubit.updateGradingLineItemId({
                          ltiGradingLineItemId,
                        })
                      }
                    />
                  </div>
                  {needsToSelectGradingItemCurrent && (
                    <div className="flex flex-row items-center gap-1 text-left text-label-large text-core-error">
                      <TriangleAlertIcon
                        className="stroke-core-error"
                        size={16}
                      />
                      {tt.lti.configuration.brightspace_select_a_grade_item()}
                    </div>
                  )}
                  {!needsToSelectGradingItemCurrent && (
                    <div className="flex flex-row items-center gap-1 text-left text-label-large text-core-success">
                      <CheckIcon className="stroke-core-success" size={16} />
                      {tt.lti.configuration.brightspace_grade_item_selected()}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-5">
              <BreakoutButton
                kind="accent"
                type="submit"
                size="medium"
                disabled={needsToSelectGradingItemCurrent}
                className="bg-fixed-accent-color"
                onClick={() => {
                  setRefreshError(false)
                  if (shouldRefreshParent) {
                    refreshParentPage()
                  } else {
                    onClick()
                  }
                }}
              >
                {tt.lti.configuration.go_to_assignment()}
              </BreakoutButton>
              {refreshError && (
                <BreakoutAlert
                  variant="warning"
                  className="mx-auto mt-4 max-w-[420px] text-left"
                >
                  {tt.lti.configuration.brightspace_refresh_prompt()}
                </BreakoutAlert>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }
)
